import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-[background,color,border-color,box-shadow,transform] duration-150 ease-out disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)] [&_svg]:size-4 [&_svg]:shrink-0 active:translate-y-px',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-primary)] text-[var(--color-paper)] hover:bg-[var(--color-primary-600)] shadow-[0_1px_0_0_rgba(0,0,0,0.1)]',
        accent:
          'bg-[var(--color-accent)] text-[var(--color-paper)] hover:bg-[var(--color-accent-600)] shadow-[0_1px_0_0_rgba(0,0,0,0.1)]',
        outline:
          'border border-[var(--color-hairline-strong)] bg-transparent text-[var(--color-ink)] hover:bg-[var(--color-paper-dim)]',
        ghost:
          'bg-transparent text-[var(--color-ink)] hover:bg-[var(--color-paper-dim)]',
        link:
          'bg-transparent text-[var(--color-primary)] underline-offset-4 hover:underline p-0 h-auto',
        danger:
          'bg-[var(--color-danger)] text-[var(--color-paper)] hover:opacity-90',
        subtle:
          'bg-[var(--color-paper-dim)] text-[var(--color-ink)] hover:bg-[var(--color-paper-deep)]',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4',
        lg: 'h-11 px-6 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
